import { useChartColors } from '@ovComponents/2-component/pieChart/getChartColors';
import { PieChart } from '@ovComponents/2-component/pieChart/pieChart';
import { RiskLevelBox } from '@ovComponents/3-pattern/riskLevelBox/riskLevelBox';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { Box, Divider, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';

export const RiskProfileVisual = ({
  options,
  riskProfileData,
  loading,
  continueFunc,
}: {
  options: any;
  riskProfileData?: any;
  loading: boolean;
  continueFunc: any;
}) => {
  const { translateBackendString } = useLocalization();
  const getMarkdown = (): string => {
    if (options?.filledTemplate) {
      return translateBackendString(options?.filledTemplate);
    }
    return translateBackendString(options?.template);
  };

  const { t } = useTranslation('riskProfile');
  const { children } = riskProfileData;
  let dataPercentage: number[] = [];
  let dataLabels: string[] = [];
  const groupedData: Record<
    string,
    { percentage: number; translatedName: string }
  > = {};
  const submit = async () => {
    await continueFunc();
  };

  const colors = useChartColors();

  if (children?.length > 0) {
    children.forEach(({ percentage, financialProduct }: any) => {
      const { key, translatedName } = financialProduct.primaryAssetClass;

      if (groupedData[key]) {
        groupedData[key].percentage += percentage;
      } else {
        groupedData[key] = {
          percentage,
          translatedName: translateBackendString(translatedName),
        };
      }
    });

    const sortedData = Object.entries(groupedData)
      .map(([key, value]) => ({
        key,
        dataPercentage: value.percentage,
        dataLabels: value.translatedName,
      }))
      .sort((a, b) => b.dataPercentage - a.dataPercentage);

    dataPercentage = Object.values(sortedData).map(
      (group) => group.dataPercentage,
    );
    dataLabels = Object.values(sortedData).map((group) => group.dataLabels);
  }

  const riskProfileIsnotAvailable = t('riskProfileIsNotAvailable');
  const riskLevel = riskProfileData?.riskProfileName
    ? riskProfileData?.riskProfileName
    : riskProfileIsnotAvailable;
  const riskLevelDescription = riskProfileData?.riskProfileDescription || '';
  const isEmptyData = dataPercentage.length === 0 && dataLabels.length === 0;

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('workflowCompletions:continue'),
        disabled: loading,
        dataTestId: 'continue-button',
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1, mb: 3 }}>
        {translateBackendString(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        {translateBackendString(options?.subtitle)}
      </Typography>
      {!loading ? (
        <RiskLevelBox
          riskLevel={riskLevel}
          riskLevelDescription={riskLevelDescription}
        ></RiskLevelBox>
      ) : null}

      {options?.displayAssetMix && !isEmptyData && (
        <>
          <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
            {t('recommendedAssetMix')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'start',
              gap: 4,
              paddingTop: '5%',
            }}
          >
            <Box
              sx={{
                width: '30%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <PieChart
                datasets={[
                  {
                    label: '',
                    data: dataPercentage,
                    backgroundColors: colors,
                  },
                ]}
                labels={dataLabels}
                height='165px'
                width='165px'
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              {dataLabels.map((label, index) => (
                <Box
                  key={label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '2%',
                  }}
                >
                  <Box
                    sx={{
                      width: 15,
                      height: 15,
                      backgroundColor: colors[index],
                      borderRadius: '25%',
                      marginRight: '10px',
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      flexGrow: 1,
                      marginRight: '25px',
                    }}
                  >
                    <Typography variant='bodySmall' component='p'>
                      {label}{' '}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant='bodySmall'
                      component='p'
                      sx={{ fontWeight: 600 }}
                    >
                      {dataPercentage[index]}%
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}
      {options?.factorsAffectingScore && !isEmptyData && (
        <>
          <Box>
            <Typography
              variant='bodyLarge'
              sx={{ fontWeight: 600, mb: 1, paddingTop: '5%' }}
            >
              {t('factorAffectingScore')}
            </Typography>
            <Divider />
            {options?.template && (
              <>
                <Typography
                  variant='bodySmall'
                  component='p'
                  sx={{
                    mt: 1,
                    mb: 2,
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '0.5px',
                  }}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    linkTarget={'_blank'}
                  >
                    {getMarkdown()}
                  </ReactMarkdown>
                </Typography>
              </>
            )}
          </Box>
          {options?.displayInvestmentKnowledge && (
            <Typography
              sx={{
                lineHeight: '20px',
                letterSpacing: '0.5px',
                mb: 2,
                mr: 1,
                fontWeight: 500,
              }}
              variant='bodyMedium'
            >
              {t('investmentKnowledge')} :
              <Box
                sx={{
                  ml: 1,
                  fontWeight: 'bold',
                  display: 'inline-block',
                }}
              >
                {t(
                  `investmentKnowledgeOptions.${riskProfileData.investmentKnowledge}`,
                )}
              </Box>
            </Typography>
          )}
          {options?.displayTimeHorizon &&
            riskProfileData.timeHorizon !== null && (
              <Typography
                sx={{ mb: 2, mr: 1, fontWeight: 500 }}
                variant='bodyMedium'
              >
                {t('timeHorizon')} :
                <Box
                  sx={{
                    ml: 1,
                    fontWeight: 'bold',
                    display: 'inline-block',
                  }}
                >
                  {t(`goalTimeHorizon.${riskProfileData.timeHorizon}`)}
                </Box>
              </Typography>
            )}
        </>
      )}
    </WorkflowCompletionForm>
  );
};
